import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { useAuthContext } from './components/AuthProvider';
import { useTheme } from './hooks/useTheme';
import './BrainPage.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/* ------------------------------------------------------------------ */
/*  Coloring — TRIBE v2 demo rendering pipeline                       */
/* ------------------------------------------------------------------ */

function brainBaseColor(sulcDepth) {
  const s = Math.max(0, Math.min(1, sulcDepth));
  const sulcus = [0.28, 0.27, 0.30];
  const gyrus  = [0.62, 0.58, 0.58];
  return [
    sulcus[0] + (gyrus[0] - sulcus[0]) * s,
    sulcus[1] + (gyrus[1] - sulcus[1]) * s,
    sulcus[2] + (gyrus[2] - sulcus[2]) * s,
  ];
}

function fireColor(t) {
  const v = Math.max(0, Math.min(1, t));
  const stops = [
    [0.00, 0.00, 0.00, 0.00],
    [0.10, 0.22, 0.01, 0.00],
    [0.25, 0.52, 0.03, 0.01],
    [0.40, 0.82, 0.13, 0.01],
    [0.55, 0.98, 0.33, 0.01],
    [0.70, 1.00, 0.58, 0.07],
    [0.82, 1.00, 0.78, 0.24],
    [0.92, 1.00, 0.92, 0.52],
    [1.00, 1.00, 1.00, 0.80],
  ];
  let lo = stops[0], hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (v >= stops[i][0] && v <= stops[i + 1][0]) {
      lo = stops[i]; hi = stops[i + 1]; break;
    }
  }
  const f = (hi[0] - lo[0]) === 0 ? 0 : (v - lo[0]) / (hi[0] - lo[0]);
  return [
    lo[1] + (hi[1] - lo[1]) * f,
    lo[2] + (hi[2] - lo[2]) * f,
    lo[3] + (hi[3] - lo[3]) * f,
  ];
}

const VMIN = 0.6;
const ALPHA_SCALE = 0.2;

function updateMeshColors(mesh, predictions, step, sulcDepth) {
  if (!mesh || !predictions) return 0;
  const colorAttr = mesh.geometry.getAttribute('color');
  const data = predictions.data[step % predictions.n_timesteps];
  let maxAct = 0;
  for (let i = 0; i < data.length && i < colorAttr.count; i++) {
    const raw = data[i];
    if (raw > maxAct) maxAct = raw;
    const sd = sulcDepth ? (sulcDepth[i] ?? 0.5) : 0.5;
    const [bgR, bgG, bgB] = brainBaseColor(sd);
    if (raw <= VMIN) {
      colorAttr.setXYZ(i, bgR, bgG, bgB);
    } else {
      const cmapT = Math.min(1, (raw - VMIN) / (1 - VMIN));
      const [fR, fG, fB] = fireColor(cmapT);
      const alpha = Math.min(1, (raw - VMIN) / ALPHA_SCALE);
      colorAttr.setXYZ(i,
        bgR + (fR - bgR) * alpha,
        bgG + (fG - bgG) * alpha,
        bgB + (fB - bgB) * alpha,
      );
    }
  }
  colorAttr.needsUpdate = true;
  return maxAct;
}

function resetMeshColors(mesh, sulcDepth) {
  if (!mesh) return;
  const colorAttr = mesh.geometry.getAttribute('color');
  for (let i = 0; i < colorAttr.count; i++) {
    const sd = sulcDepth ? (sulcDepth[i] ?? 0.5) : 0.5;
    const [r, g, b] = brainBaseColor(sd);
    colorAttr.setXYZ(i, r, g, b);
  }
  colorAttr.needsUpdate = true;
}

/* ------------------------------------------------------------------ */
/*  Example content                                                    */
/* ------------------------------------------------------------------ */

const TEXT_EXAMPLES = [
  {
    label: 'Nature',
    text: 'The golden sun slowly sank beneath the horizon, painting the African savanna in warm hues of amber and crimson. A herd of elephants moved silently through the tall grass, their shadows stretching long across the dusty plain.',
  },
  {
    label: 'Science',
    text: 'Researchers at CERN announced today the discovery of a new subatomic particle that could change our understanding of dark matter. The particle was detected during a high-energy collision experiment and exhibits properties never before observed.',
  },
  {
    label: 'Literature',
    text: 'It was a bright cold day in April, and the clocks were striking thirteen. Winston Smith, his chin nuzzled into his breast in an effort to escape the vile wind, slipped quickly through the glass doors of Victory Mansions.',
  },
];

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

function engagementFromRaw(rawScores) {
  // Map raw RMS activation values from TRIBE v2 to 0-100.
  // Calibrated from real predictions: RMS ~0.10 = low, ~0.15 = mid, ~0.25 = high.
  return rawScores.map(rms => {
    const score = 100 / (1 + Math.exp(-30 * (rms - 0.15)));
    return Math.round(Math.max(0, Math.min(100, score)));
  });
}


const ONBOARDING_KEY = 'bp_onboarding_seen';

function BrainPage() {
  const [meshData, setMeshData] = useState(null);
  const [meshLoading, setMeshLoading] = useState(true);
  const [inputMode, setInputMode] = useState('video');
  const [textInput, setTextInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [currentName, setCurrentName] = useState('');
  const [playing, setPlaying] = useState(false);
  const [timeStep, setTimeStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [maxActivation, setMaxActivation] = useState(0);
  const [segments, setSegments] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [analysisText, setAnalysisText] = useState('');
  const [analysisType, setAnalysisType] = useState('video');
  const [engagementScores, setEngagementScores] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(ONBOARDING_KEY));
  const [onboardingStep, setOnboardingStep] = useState(0);

  const [videoAspect, setVideoAspect] = useState(null);
  const [estimatedSeconds, setEstimatedSeconds] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const animFrameRef = useRef(null);
  const brainMeshRef = useRef(null);
  const predictionsRef = useRef(null);
  const sulcDepthRef = useRef(null);
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const timeRef = useRef(0);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const elapsedRef = useRef(null);
  const hasUsedFreeAnalysisRef = useRef(false);

  const { user, openAuthModal } = useAuthContext();
  const isDarkTheme = useTheme();
  const promoLogoSrc = isDarkTheme ? '/latted_white.png' : '/latted_black.png';

  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { speedRef.current = speed; }, [speed]);

  /* ── Fetch mesh on mount ─────────────────────────────── */
  useEffect(() => {
    setMeshLoading(true);
    fetch(`${API_BASE}/api/brain/mesh`)
      .then(r => r.json())
      .then(data => {
        const mesh = data.mesh || data;
        if (mesh.vertices) setMeshData(mesh);
      })
      .catch(() => {})
      .finally(() => setMeshLoading(false));
  }, []);

  /* ── Three.js scene (runs once when mesh loads) ──────── */
  useEffect(() => {
    if (!meshData || !canvasRef.current) return;
    const canvas = canvasRef.current;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    } catch { return; }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b0d12);

    const camera = new THREE.PerspectiveCamera(50, 1, 1, 2000);

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    rendererRef.current = renderer;

    const { vertices, faces } = meshData;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(vertices.length * 3);
    for (let i = 0; i < vertices.length; i++) {
      positions[i * 3]     = vertices[i][0];
      positions[i * 3 + 1] = vertices[i][1];
      positions[i * 3 + 2] = vertices[i][2];
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const indices = new Uint32Array(faces.length * 3);
    for (let i = 0; i < faces.length; i++) {
      indices[i * 3]     = faces[i][0];
      indices[i * 3 + 1] = faces[i][1];
      indices[i * 3 + 2] = faces[i][2];
    }
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    const sulcDepth = meshData.sulc_depth || null;
    sulcDepthRef.current = sulcDepth;

    const colors = new Float32Array(vertices.length * 3);
    for (let i = 0; i < vertices.length; i++) {
      const sd = sulcDepth ? sulcDepth[i] : 0.5;
      const [r, g, b] = brainBaseColor(sd);
      colors[i * 3]     = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    geometry.rotateX(-Math.PI / 2);

    geometry.computeBoundingBox();
    const center = new THREE.Vector3();
    geometry.boundingBox.getCenter(center);
    geometry.translate(-center.x, -center.y, -center.z);
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    const radius = geometry.boundingSphere.radius;
    const fov = camera.fov * (Math.PI / 180);
    const camDist = (radius / Math.sin(fov / 2)) * 1.5;
    camera.position.set(0, radius * 0.15, camDist);
    camera.lookAt(0, 0, 0);
    camera.near = camDist * 0.01;
    camera.far = camDist * 10;

    // Size renderer to actual container dimensions (flex layout may not be ready yet)
    const syncSize = () => {
      const rw = canvas.clientWidth;
      const rh = canvas.clientHeight;
      if (rw > 0 && rh > 0) {
        renderer.setSize(rw, rh);
        camera.aspect = rw / rh;
        camera.updateProjectionMatrix();
      }
    };
    syncSize();
    requestAnimationFrame(syncSize);

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.rotateSpeed = 0.5;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.4;
    controls.minDistance = camDist * 0.4;
    controls.maxDistance = camDist * 2.5;
    controls.target.set(0, 0, 0);

    scene.add(new THREE.AmbientLight(0x404060, 0.6));
    const keyLight = new THREE.DirectionalLight(0xfff0e0, 1.1);
    keyLight.position.set(radius, radius * 1.5, radius * 1.2);
    scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(0xb0c0e0, 0.45);
    fillLight.position.set(-radius * 1.2, radius * 0.5, -radius * 0.5);
    scene.add(fillLight);
    const rimLight = new THREE.DirectionalLight(0x886644, 0.5);
    rimLight.position.set(0, -radius * 0.3, -radius);
    scene.add(rimLight);
    const topLight = new THREE.DirectionalLight(0xe0e8ff, 0.35);
    topLight.position.set(0, radius * 2, 0);
    scene.add(topLight);
    const cameraLight = new THREE.PointLight(0xffffff, 0.15, camDist * 3);
    scene.add(cameraLight);

    const material = new THREE.MeshPhysicalMaterial({
      vertexColors: true,
      roughness: 0.55,
      metalness: 0.0,
      clearcoat: 0.15,
      clearcoatRoughness: 0.5,
      sheen: 0.3,
      sheenRoughness: 0.7,
      sheenColor: new THREE.Color(0x6b5540),
      side: THREE.DoubleSide,
    });
    const brainMesh = new THREE.Mesh(geometry, material);
    scene.add(brainMesh);
    brainMeshRef.current = brainMesh;

    const glowGeo = geometry.clone();
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0x8b5e3c, transparent: true, opacity: 0.03,
      side: THREE.FrontSide, depthWrite: false,
    });
    const glowMesh = new THREE.Mesh(glowGeo, glowMat);
    glowMesh.scale.setScalar(1.008);
    scene.add(glowMesh);

    const pCount = 1500;
    const pPositions = new Float32Array(pCount * 3);
    const pColors = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount; i++) {
      const r2 = radius * (1.3 + Math.random() * 2.5);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pPositions[i * 3]     = r2 * Math.sin(phi) * Math.cos(theta);
      pPositions[i * 3 + 1] = r2 * Math.sin(phi) * Math.sin(theta);
      pPositions[i * 3 + 2] = r2 * Math.cos(phi);
      const br = 0.12 + Math.random() * 0.2;
      pColors[i * 3]     = 0.6 * br;
      pColors[i * 3 + 1] = 0.35 * br;
      pColors[i * 3 + 2] = 0.15 * br;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPositions, 3));
    pGeo.setAttribute('color', new THREE.BufferAttribute(pColors, 3));
    scene.add(new THREE.Points(pGeo, new THREE.PointsMaterial({
      size: 0.4, vertexColors: true, transparent: true, opacity: 0.25,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })));

    const handleResize = () => {
      const rw = canvas.clientWidth;
      const rh = canvas.clientHeight;
      if (rw && rh) {
        camera.aspect = rw / rh;
        camera.updateProjectionMatrix();
        renderer.setSize(rw, rh);
      }
    };
    window.addEventListener('resize', handleResize);

    const clock = new THREE.Clock();
    let lastStepTime = 0;

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      const elapsed = clock.getElapsedTime();
      controls.update();

      if (brainMeshRef.current && playingRef.current && predictionsRef.current) {
        const stepDuration = 0.5 / speedRef.current;
        if (elapsed - lastStepTime > stepDuration) {
          lastStepTime = elapsed;
          const preds = predictionsRef.current;
          timeRef.current = (timeRef.current + 1) % preds.n_timesteps;
          setTimeStep(timeRef.current);
          const ma = updateMeshColors(brainMeshRef.current, preds, timeRef.current, sulcDepthRef.current);
          setMaxActivation(ma.toFixed(2));
        }
      }

      cameraLight.position.copy(camera.position);
      if (brainMeshRef.current) {
        const breath = 1.0 + 0.002 * Math.sin(elapsed * 1.2);
        brainMeshRef.current.scale.setScalar(breath);
        glowMesh.scale.setScalar(breath * 1.008);
        glowMat.opacity = 0.02 + 0.015 * Math.sin(elapsed * 1.8);
      }
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      glowGeo.dispose();
      glowMat.dispose();
      pGeo.dispose();
    };
  }, [meshData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Handlers ────────────────────────────────────────── */

  const handleAnalysisResponse = useCallback((data, name, type) => {
    predictionsRef.current = data.predictions;
    setTotalSteps(data.predictions.n_timesteps);
    setTimeStep(0);
    timeRef.current = 0;
    setSegments(data.segments || []);
    setCurrentName(name);
    setAnalysisType(type || 'video');
    setEngagementScores(
      data.engagement_raw ? engagementFromRaw(data.engagement_raw) : []
    );
    setProcessing(false);
    setPlaying(true);
    playingRef.current = true;
    hasUsedFreeAnalysisRef.current = true;
    if (brainMeshRef.current) {
      const ma = updateMeshColors(brainMeshRef.current, data.predictions, 0, sulcDepthRef.current);
      setMaxActivation(ma.toFixed(2));
    }
  }, []);

  const getVideoDuration = useCallback((file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const el = document.createElement('video');
      el.preload = 'metadata';
      el.onloadedmetadata = () => { resolve(el.duration || 0); URL.revokeObjectURL(url); };
      el.onerror = () => { resolve(0); URL.revokeObjectURL(url); };
      el.src = url;
    });
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a video file (MP4, MOV, WebM, AVI)');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError('Video must be under 100MB');
      return;
    }
    if (hasUsedFreeAnalysisRef.current && !user) {
      setError('Sign in to run more analyses');
      openAuthModal();
      return;
    }
    setError(null);
    setProcessing(true);
    setCurrentName(file.name);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(URL.createObjectURL(file));

    const duration = await getVideoDuration(file);
    const estSec = duration > 0 ? Math.max(60, Math.round(duration * 12)) : 0;
    setEstimatedSeconds(estSec);
    setElapsedSeconds(0);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    elapsedRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    const steps = [
      'Extracting video frames & audio...',
      'Transcribing speech with WhisperX...',
      'Extracting visual features (V-JEPA2)...',
      'Extracting audio features (Wav2Vec-BERT)...',
      'Extracting text features (LLaMA 3.2)...',
      'Running TRIBE v2 Transformer encoder...',
      'Mapping predictions to cortical surface...',
    ];
    let stepIdx = 0;
    setProcessingStep(steps[0]);
    const stepDuration = estSec > 0 ? (estSec / steps.length) * 1000 : 2000;
    const stepInterval = setInterval(() => {
      stepIdx++;
      if (stepIdx < steps.length) setProcessingStep(steps[stepIdx]);
    }, stepDuration);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${API_BASE}/api/brain/analyze`, { method: 'POST', body: formData });
      clearInterval(stepInterval);
      clearInterval(elapsedRef.current);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error('Analysis could not be completed. Please try again.');
      }
      const data = await resp.json();
      if (!data.success) throw new Error('Analysis could not be completed. Please try again.');
      handleAnalysisResponse(data, file.name, 'video');
    } catch (err) {
      clearInterval(stepInterval);
      clearInterval(elapsedRef.current);
      console.error('Video analysis failed:', err);
      setError('Analysis could not be completed. Please try again.');
      setProcessing(false);
    }
  }, [handleAnalysisResponse, videoUrl, getVideoDuration, user, openAuthModal]);

  const handleTextAnalyze = useCallback(async (overrideText) => {
    const text = (overrideText || textInput).trim();
    if (!text) { setError('Please enter some text'); return; }
    if (hasUsedFreeAnalysisRef.current && !user) {
      setError('Sign in to run more analyses');
      openAuthModal();
      return;
    }
    setError(null);
    setProcessing(true);
    setCurrentName(overrideText ? 'Text example' : 'Text input');
    setAnalysisText(text);
    if (videoUrl) { URL.revokeObjectURL(videoUrl); setVideoUrl(null); }

    const steps = [
      'Converting text to speech (TTS)...',
      'Extracting audio features (Wav2Vec-BERT)...',
      'Extracting text features (LLaMA 3.2)...',
      'Running TRIBE v2 Transformer encoder...',
      'Mapping predictions to cortical surface...',
    ];
    let stepIdx = 0;
    setProcessingStep(steps[0]);
    const stepInterval = setInterval(() => {
      stepIdx++;
      if (stepIdx < steps.length) setProcessingStep(steps[stepIdx]);
    }, 2000);

    try {
      const resp = await fetch(`${API_BASE}/api/brain/analyze-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      clearInterval(stepInterval);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error('Analysis could not be completed. Please try again.');
      }
      const data = await resp.json();
      if (!data.success) throw new Error('Analysis could not be completed. Please try again.');
      handleAnalysisResponse(data, overrideText ? 'Text example' : 'Text input', 'text');
    } catch (err) {
      clearInterval(stepInterval);
      console.error('Text analysis failed:', err);
      setError('Analysis could not be completed. Please try again.');
      setProcessing(false);
    }
  }, [textInput, handleAnalysisResponse, videoUrl, user, openAuthModal]);

  const handleTextExample = useCallback((example) => {
    setTextInput(example.text);
    handleTextAnalyze(example.text);
  }, [handleTextAnalyze]);

  const handleClear = useCallback(() => {
    predictionsRef.current = null;
    playingRef.current = false;
    setPlaying(false);
    timeRef.current = 0;
    setTimeStep(0);
    setTotalSteps(0);
    setMaxActivation(0);
    setCurrentName('');
    setSegments([]);
    setError(null);
    setEngagementScores([]);
    setAnalysisText('');
    setAnalysisType('video');
    if (videoUrl) { URL.revokeObjectURL(videoUrl); setVideoUrl(null); }
    setVideoAspect(null);
    setEstimatedSeconds(0);
    setElapsedSeconds(0);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    resetMeshColors(brainMeshRef.current, sulcDepthRef.current);
  }, [videoUrl]);

  const handleTimelineChange = (e) => {
    const step = parseInt(e.target.value, 10);
    setTimeStep(step);
    timeRef.current = step;
    if (brainMeshRef.current && predictionsRef.current) {
      const ma = updateMeshColors(brainMeshRef.current, predictionsRef.current, step, sulcDepthRef.current);
      setMaxActivation(ma.toFixed(2));
    }
  };

  const cycleSpeed = () => {
    const speeds = [0.5, 1, 2, 4];
    const idx = speeds.indexOf(speed);
    setSpeed(speeds[(idx + 1) % speeds.length]);
  };

  const onDragOver = useCallback((e) => { e.preventDefault(); setDragging(true); }, []);
  const onDragLeave = useCallback((e) => { e.preventDefault(); setDragging(false); }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  // Sync video to brain timestep
  useEffect(() => {
    if (videoRef.current && totalSteps > 0) {
      try { videoRef.current.currentTime = timeStep; } catch {}
    }
  }, [timeStep, totalSteps]);

  const currentSegment = segments[timeStep] || null;
  const segmentText = currentSegment?.events?.map(ev => ev.text).filter(Boolean).join(' ') || '';
  const hasData = totalSteps > 0;
  const liveEngagement = engagementScores[timeStep] ?? 0;
  const overallEngagement = engagementScores.length
    ? Math.round(engagementScores.reduce((a, b) => a + b, 0) / engagementScores.length)
    : 0;

  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_KEY, '1');
  }, []);

  const onboardingNext = useCallback(() => {
    if (onboardingStep < 2) setOnboardingStep(s => s + 1);
    else dismissOnboarding();
  }, [onboardingStep, dismissOnboarding]);

  const ONBOARDING_STEPS = [
    {
      title: 'Welcome to Brain Explorer',
      body: 'See how any video or text lights up the human brain — in real time. Powered by Meta\'s TRIBE v2 foundation model, this tool predicts whole-brain fMRI responses to the content you feed it.',
      visual: (
        <div className="bp-ob__visual bp-ob__visual--welcome">
          <svg viewBox="0 0 320 180" className="bp-ob__hero-svg">
            <defs>
              <radialGradient id="obGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--color-accent-base, #d98f4f)" stopOpacity="0.35" />
                <stop offset="70%" stopColor="var(--color-accent-base, #d98f4f)" stopOpacity="0.08" />
                <stop offset="100%" stopColor="transparent" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="obFire" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#1a0000" />
                <stop offset="30%" stopColor="#d91a00" />
                <stop offset="60%" stopColor="#ff8800" />
                <stop offset="100%" stopColor="#ffffb3" />
              </linearGradient>
            </defs>
            <ellipse cx="160" cy="90" rx="120" ry="80" fill="url(#obGlow)" />
            {/* Stylized brain silhouette */}
            <path d="M160,30 C120,30 90,50 85,80 C80,105 95,130 115,140 C125,145 140,150 160,150 C180,150 195,145 205,140 C225,130 240,105 235,80 C230,50 200,30 160,30Z" fill="none" stroke="var(--color-text-secondary)" strokeWidth="1.5" opacity="0.3" />
            <path d="M160,30 C160,70 140,80 130,90 C120,100 125,120 140,130" fill="none" stroke="var(--color-text-secondary)" strokeWidth="1" opacity="0.2" />
            <path d="M160,30 C160,70 180,80 190,90 C200,100 195,120 180,130" fill="none" stroke="var(--color-text-secondary)" strokeWidth="1" opacity="0.2" />
            {/* Activity hotspots */}
            <circle cx="130" cy="70" r="14" fill="url(#obFire)" opacity="0.7" className="bp-ob__pulse" />
            <circle cx="185" cy="85" r="10" fill="url(#obFire)" opacity="0.55" className="bp-ob__pulse bp-ob__pulse--d1" />
            <circle cx="150" cy="105" r="8" fill="url(#obFire)" opacity="0.45" className="bp-ob__pulse bp-ob__pulse--d2" />
            <circle cx="175" cy="60" r="6" fill="url(#obFire)" opacity="0.35" className="bp-ob__pulse bp-ob__pulse--d3" />
            {/* TRIBE v2 label */}
            <text x="160" y="172" textAnchor="middle" fontSize="10" fontWeight="700" letterSpacing="2" fill="var(--color-text-secondary)" opacity="0.5">TRIBE v2</text>
          </svg>
        </div>
      ),
    },
    {
      title: 'How It Works',
      body: 'Upload a video or enter text. TRIBE v2 extracts visual features (V-JEPA2), audio features (Wav2Vec-BERT), and language features (LLaMA 3.2) — then fuses them through a Transformer encoder to predict cortical activity across 20,484 brain vertices.',
      visual: (
        <div className="bp-ob__visual bp-ob__visual--pipeline">
          <div className="bp-ob__pipeline">
            <div className="bp-ob__pipe-step">
              <div className="bp-ob__pipe-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="4" />
                  <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
                </svg>
              </div>
              <span className="bp-ob__pipe-label">Upload</span>
              <span className="bp-ob__pipe-sub">Video or Text</span>
            </div>
            <div className="bp-ob__pipe-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-base, #d98f4f)" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
            </div>
            <div className="bp-ob__pipe-step">
              <div className="bp-ob__pipe-icon bp-ob__pipe-icon--accent">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              </div>
              <span className="bp-ob__pipe-label">AI Extraction</span>
              <span className="bp-ob__pipe-sub">V-JEPA2 · Wav2Vec · LLaMA</span>
            </div>
            <div className="bp-ob__pipe-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-base, #d98f4f)" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
            </div>
            <div className="bp-ob__pipe-step">
              <div className="bp-ob__pipe-icon bp-ob__pipe-icon--glow">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a7 7 0 0 0-7 7c0 3 2 5.5 4 7.5.6.6 1 1.3 1.2 2h3.6c.2-.7.6-1.4 1.2-2 2-2 4-4.5 4-7.5a7 7 0 0 0-7-7z" />
                  <path d="M9 21h6M10 17h4" />
                </svg>
              </div>
              <span className="bp-ob__pipe-label">Brain Map</span>
              <span className="bp-ob__pipe-sub">20,484 vertices</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Read the Results',
      body: 'Watch activity sweep across the 3D cortical surface in real time. The engagement score (0–100) tells you how stimulating each moment is. Use playback controls to scrub through time, adjust speed, and compare different content.',
      visual: (
        <div className="bp-ob__visual bp-ob__visual--results">
          <div className="bp-ob__results-row">
            <div className="bp-ob__result-card">
              <div className="bp-ob__result-icon">
                <svg viewBox="0 0 48 48" width="36" height="36">
                  <defs>
                    <linearGradient id="obBar" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="var(--color-accent-base, #d98f4f)" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="var(--color-accent-base, #d98f4f)" stopOpacity="0.6" />
                    </linearGradient>
                  </defs>
                  <rect x="4" y="28" width="6" height="16" rx="2" fill="url(#obBar)" />
                  <rect x="14" y="18" width="6" height="26" rx="2" fill="url(#obBar)" />
                  <rect x="24" y="10" width="6" height="34" rx="2" fill="var(--color-accent-base, #d98f4f)" opacity="0.8" />
                  <rect x="34" y="22" width="6" height="22" rx="2" fill="url(#obBar)" />
                </svg>
              </div>
              <span className="bp-ob__result-label">Engagement Score</span>
              <span className="bp-ob__result-hint">0 – 100 per timestep</span>
            </div>
            <div className="bp-ob__result-card">
              <div className="bp-ob__result-icon">
                <svg viewBox="0 0 48 48" width="36" height="36" fill="none" stroke="var(--color-accent-base, #d98f4f)" strokeWidth="2" strokeLinecap="round">
                  <circle cx="24" cy="24" r="16" opacity="0.2" />
                  <path d="M20 18l10 6-10 6z" fill="var(--color-accent-base, #d98f4f)" stroke="none" opacity="0.7" />
                </svg>
              </div>
              <span className="bp-ob__result-label">Playback Controls</span>
              <span className="bp-ob__result-hint">Play, scrub & adjust speed</span>
            </div>
            <div className="bp-ob__result-card">
              <div className="bp-ob__result-icon">
                <svg viewBox="0 0 48 48" width="36" height="36">
                  <rect x="3" y="38" width="42" height="5" rx="2.5" fill="none" stroke="var(--color-text-secondary)" strokeWidth="1.5" opacity="0.3" />
                  <rect x="3" y="38" width="28" height="5" rx="2.5">
                    <animate attributeName="width" values="10;28;18;28" dur="3s" repeatCount="indefinite" />
                  </rect>
                  <defs>
                    <linearGradient id="obHeat" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#1a0000" />
                      <stop offset="40%" stopColor="#d91a00" />
                      <stop offset="70%" stopColor="#ff8800" />
                      <stop offset="100%" stopColor="#ffffb3" />
                    </linearGradient>
                  </defs>
                  <rect x="3" y="38" width="42" height="5" rx="2.5" fill="url(#obHeat)" opacity="0.85">
                    <animate attributeName="opacity" values="0.6;0.85;0.6" dur="2s" repeatCount="indefinite" />
                  </rect>
                  <text x="24" y="30" textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--color-text-secondary)" opacity="0.7">ACTIVITY</text>
                </svg>
              </div>
              <span className="bp-ob__result-label">Heatmap Overlay</span>
              <span className="bp-ob__result-hint">Fire colormap on 3D brain</span>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const step = ONBOARDING_STEPS[onboardingStep];

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div className="bp">
      {/* ─── Onboarding modal ─── */}
      {showOnboarding && (
        <div className="bp-ob" onClick={dismissOnboarding}>
          <div className="bp-ob__card" onClick={e => e.stopPropagation()}>
            <button className="bp-ob__close" onClick={dismissOnboarding} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>

            {step.visual}

            <div className="bp-ob__content">
              <h2 className="bp-ob__title">{step.title}</h2>
              <p className="bp-ob__body">{step.body}</p>
            </div>

            <div className="bp-ob__footer">
              <div className="bp-ob__dots">
                {ONBOARDING_STEPS.map((_, i) => (
                  <button
                    key={i}
                    className={`bp-ob__dot${i === onboardingStep ? ' bp-ob__dot--active' : ''}`}
                    onClick={() => setOnboardingStep(i)}
                  />
                ))}
              </div>
              <div className="bp-ob__actions">
                {onboardingStep > 0 && (
                  <button className="bp-ob__btn bp-ob__btn--ghost" onClick={() => setOnboardingStep(s => s - 1)}>
                    Back
                  </button>
                )}
                <button className="bp-ob__btn bp-ob__btn--primary" onClick={onboardingNext}>
                  {onboardingStep < 2 ? 'Next' : 'Get Started'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Left: Brain visualization ─── */}
      <div className="bp__left">
        {meshLoading && (
          <div className="bp__mesh-loading">
            <div className="bp__spinner" />
            <span>Loading brain model...</span>
          </div>
        )}
        <canvas ref={canvasRef} className="bp__canvas" />

        <Link to="/" className="bp__page-logo">
          <img src={promoLogoSrc} alt="Latted" className="bp__page-logo-img" />
        </Link>

        {/* Colorbar */}
        <div className="bp__colorbar">
          <span className="bp__colorbar-lo">Low</span>
          <div className="bp__colorbar-bar" />
          <span className="bp__colorbar-hi">High</span>
        </div>
        <span className="bp__colorbar-title">Activity</span>

        {/* Now playing info */}
        {hasData && (
          <div className="bp__now-info">
            <span className="bp__now-name">{currentName}</span>
            <span className="bp__now-stats">Peak {maxActivation} &middot; t={timeStep + 1}/{totalSteps}</span>
          </div>
        )}

        {/* Bottom overlay: controls + engagement */}
        {hasData && (
          <div className="bp__bottom-overlay">
            {/* Playback controls */}
            <div className="bp__controls">
              <button className="bp__ctrl-btn" onClick={() => setPlaying(p => !p)} title={playing ? 'Pause' : 'Play'}>
                {playing ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                )}
              </button>
              <span className="bp__ctrl-time">{timeStep + 1}</span>
              <input type="range" min="0" max={Math.max(0, totalSteps - 1)}
                value={timeStep} onChange={handleTimelineChange}
                className="bp__ctrl-slider" />
              <span className="bp__ctrl-time">{totalSteps}</span>
              <button className="bp__ctrl-speed" onClick={cycleSpeed}>{speed}x</button>
              <button className="bp__ctrl-clear" onClick={handleClear}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Engagement graph */}
            <div className="bp__engagement">
              <div className="bp__eng-header">
                <span className="bp__eng-title">Engagement</span>
                <div className="bp__eng-scores">
                  <div className="bp__eng-score">
                    <span className="bp__eng-score-label">Live</span>
                    <span className={`bp__eng-score-value${liveEngagement > 60 ? ' bp__eng-score--high' : ''}`}>{liveEngagement}</span>
                  </div>
                  <div className="bp__eng-score">
                    <span className="bp__eng-score-label">Overall</span>
                    <span className="bp__eng-score-value">{overallEngagement}</span>
                  </div>
                </div>
              </div>
              {engagementScores.length > 1 && (() => {
                const n = engagementScores.length;
                const mx = Math.max(...engagementScores, 1);
                const W = 500, H = 100, PAD = 4;
                const pts = engagementScores.map((s, i) => [
                  PAD + (i / (n - 1)) * (W - PAD * 2),
                  H - PAD - (s / mx) * (H - PAD * 2),
                ]);
                const line = pts.map(p => p.join(',')).join(' ');
                const area = `${PAD},${H - PAD} ${line} ${W - PAD},${H - PAD}`;
                const cx = pts[timeStep]?.[0] ?? 0;
                const cy = pts[timeStep]?.[1] ?? H / 2;
                return (
                  <svg viewBox={`0 0 ${W} ${H}`} className="bp__eng-graph" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="engFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(217,143,79,0.35)" />
                        <stop offset="100%" stopColor="rgba(217,143,79,0)" />
                      </linearGradient>
                    </defs>
                    <polygon points={area} fill="url(#engFill)" />
                    <polyline points={line} fill="none" stroke="var(--color-accent-base, #d98f4f)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
                    <line x1={cx} y1="0" x2={cx} y2={H} stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                    <circle cx={cx} cy={cy} r="6" fill="var(--color-accent-strong, #e3a36a)" stroke="var(--color-bg-canvas-surface, #0e1117)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
                  </svg>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* ─── Right: Input / Results panel ─── */}
      <div className="bp__right">
        <div className="bp__right-scroll">
          <div className="bp__header">
            <span className="bp__logo-text">Brain Explorer</span>
            <span className="bp__badge">TRIBE v2</span>
          </div>

          {/* ── Results view (when data loaded) ── */}
          {hasData && (
            <>
              {/* Video player */}
              {analysisType === 'video' && videoUrl && (
                <div className="bp__video-wrap">
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    muted
                    playsInline
                    className="bp__video"
                    style={videoAspect ? { aspectRatio: videoAspect } : undefined}
                    onLoadedMetadata={(e) => {
                      const v = e.currentTarget;
                      if (v.videoWidth && v.videoHeight) {
                        setVideoAspect(`${v.videoWidth} / ${v.videoHeight}`);
                      }
                    }}
                  />
                  <div className="bp__video-time">
                    <span>{currentName}</span>
                    <span className="bp__video-ts">t = {timeStep + 1}s</span>
                  </div>
                </div>
              )}

              {/* Video example placeholder (no real file) */}
              {analysisType === 'video' && !videoUrl && (
                <div className="bp__video-placeholder">
                  <span className="bp__video-placeholder-name">{currentName}</span>
                  <span className="bp__video-placeholder-ts">t = {timeStep + 1} / {totalSteps}s</span>
                </div>
              )}

              {/* Text display with highlighting */}
              {analysisType === 'text' && analysisText && (
                <div className="bp__text-display">
                  <div className="bp__text-display-label">Stimulus text</div>
                  <div className="bp__text-display-body">
                    {(() => {
                      const words = analysisText.split(/\s+/).filter(Boolean);
                      if (!words.length || !totalSteps) return analysisText;
                      const wordsPerStep = words.length / totalSteps;
                      const activeStart = Math.min(words.length - 1, Math.floor(timeStep * wordsPerStep));
                      const activeEnd = Math.min(words.length, Math.max(activeStart + 1, Math.ceil((timeStep + 1) * wordsPerStep)));
                      return words.map((w, i) => (
                        <span
                          key={i}
                          className={i >= activeStart && i < activeEnd ? 'bp__tw--active' : 'bp__tw--dim'}
                        >
                          {w}{' '}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              )}

              <button className="bp__new-analysis-btn" onClick={handleClear}>
                New Analysis
              </button>
            </>
          )}

          {/* ── Input view (no data) ── */}
          {!hasData && (
            <>
              {/* Tabs */}
              <div className="bp__tabs">
                <button
                  className={`bp__tab${inputMode === 'video' ? ' bp__tab--active' : ''}`}
                  onClick={() => { setInputMode('video'); setError(null); }}
                >
                  Video
                </button>
                <button
                  className={`bp__tab${inputMode === 'text' ? ' bp__tab--active' : ''}`}
                  onClick={() => { setInputMode('text'); setError(null); }}
                >
                  Text
                </button>
              </div>

              <p className="bp__desc">
                {inputMode === 'video'
                  ? "Upload a video to predict whole-brain fMRI responses using TRIBE v2. The model extracts visual, audio, and language features to map cortical activity."
                  : "Enter text to predict how the brain would respond to hearing it. TRIBE v2 converts text to speech, then models whole-brain responses."
                }
              </p>

              {error && <div className="bp__error">{error}</div>}

              {processing && (
                <div className="bp__processing">
                  <div className="bp__proc-bar">
                    <div className="bp__proc-bar-fill" style={
                      estimatedSeconds > 0
                        ? { width: `${Math.min(95, (elapsedSeconds / estimatedSeconds) * 100)}%`, transition: 'width 1s linear' }
                        : undefined
                    } />
                  </div>
                  <div className="bp__proc-text">
                    Analyzing <strong>{currentName}</strong>
                  </div>
                  <div className="bp__proc-step">{processingStep}</div>
                  {estimatedSeconds > 0 && (() => {
                    const remaining = Math.max(0, estimatedSeconds - elapsedSeconds);
                    const min = Math.floor(remaining / 60);
                    const sec = remaining % 60;
                    const timeStr = min > 0 ? `~${min}m ${sec}s` : `~${sec}s`;
                    return (
                      <div className="bp__proc-eta">
                        <span className="bp__proc-eta-time">{remaining > 0 ? `${timeStr} remaining` : 'Finishing up...'}</span>
                        <span className="bp__proc-eta-hint">Please keep this tab open</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {!processing && (
                <>
                  {inputMode === 'video' ? (
                    <div
                      className={`bp__dropzone${dragging ? ' bp__dropzone--active' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                    >
                      <span>Drop video here or <u>browse</u></span>
                      <span className="bp__drop-hint">MP4, MOV, WebM up to 100MB</span>
                      <input ref={fileInputRef} type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFile(e.target.files[0])} />
                    </div>
                  ) : (
                    <>
                      <div className="bp__text-area">
                        <textarea
                          className="bp__textarea"
                          placeholder="Enter any text — a passage, lyrics, a story..."
                          value={textInput}
                          onChange={(e) => setTextInput(e.target.value)}
                          rows={4}
                          maxLength={5000}
                        />
                        <div className="bp__text-footer">
                          <span className="bp__char-count">{textInput.length}/5000</span>
                          <button className="bp__analyze-btn"
                            onClick={() => handleTextAnalyze()}
                            disabled={!textInput.trim()}>
                            Analyze
                          </button>
                        </div>
                      </div>

                      <div className="bp__examples-section">
                        <div className="bp__examples-label">Or try an example</div>
                        <div className="bp__examples-grid bp__examples-grid--text">
                          {TEXT_EXAMPLES.map((ex, i) => (
                            <button key={i} className="bp__example-text-card" onClick={() => handleTextExample(ex)}>
                              <span className="bp__example-text-title">{ex.label}</span>
                              <span className="bp__example-text-preview">{ex.text.slice(0, 100)}...</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* UGC Automation Promo */}
          <div className="bp__promo">
            <div className="bp__promo-visual">
              <svg viewBox="0 0 380 100" className="bp__promo-chart-svg" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="promoGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-accent-base, #d98f4f)" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="var(--color-accent-base, #d98f4f)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <polygon points="0,88 30,82 60,78 100,68 140,58 180,48 220,36 260,26 300,18 340,10 380,5 380,100 0,100" fill="url(#promoGrad)" />
                <polyline points="0,88 30,82 60,78 100,68 140,58 180,48 220,36 260,26 300,18 340,10 380,5" fill="none" stroke="var(--color-accent-base, #d98f4f)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              </svg>
              <div className="bp__promo-platforms">
                <div className="bp__promo-platform bp__promo-platform--1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.88-2.89 2.89 2.89 0 0 1 2.88-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.75a8.18 8.18 0 0 0 4.76 1.52V6.84a4.84 4.84 0 0 1-1-.15z"/></svg>
                </div>
                <div className="bp__promo-platform bp__promo-platform--2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                </div>
                <div className="bp__promo-platform bp__promo-platform--3">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </div>
              </div>
              <div className="bp__promo-stats">
                <div className="bp__promo-stat">
                  <span className="bp__promo-stat-val">2.4M</span>
                  <span className="bp__promo-stat-lbl">views/mo</span>
                </div>
                <div className="bp__promo-stat">
                  <span className="bp__promo-stat-val">8.2%</span>
                  <span className="bp__promo-stat-lbl">eng. rate</span>
                </div>
                <div className="bp__promo-stat">
                  <span className="bp__promo-stat-val">340%</span>
                  <span className="bp__promo-stat-lbl">ROAS</span>
                </div>
              </div>
            </div>
            <div className="bp__promo-body">
              <div className="bp__promo-brand">
                <img src={promoLogoSrc} alt="Latted" className="bp__promo-logo" />
                <span className="bp__promo-label">UGC AUTOMATION</span>
              </div>
              <h3 className="bp__promo-title">Turn Engagement Data into Viral Content</h3>
              <p className="bp__promo-desc">
                Fuel automated social accounts with brain-backed engagement insights.
                Generate & publish high-converting UGC to TikTok, Instagram & YouTube on autopilot.
              </p>
              <Link to="/" className="bp__promo-cta">
                Explore Automation
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>

          <div className="bp__footer">
            Powered by{' '}
            <a href="https://ai.meta.com/blog/tribe-v2-brain-predictive-foundation-model/" target="_blank" rel="noreferrer">
              Meta TRIBE v2
            </a>
            {' · '}fsaverage5 surface · 20,484 vertices
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrainPage;
